import React, { FormEvent, useEffect, useState } from 'react';
import './payInvoice.scss';
import { GuestUserContext } from '@chr/navisphereonline-shared-components';
import { getPoliciesWithPermission, LinkService } from '@chr/online-shared-microsite-core';
import { Redirect } from 'react-router';
import { getPaymentAuthToken } from '../../common/apis/paymentsV2Api';
import { LocalConfig } from '@/common/constants/localConfig';
import { PermissionIds } from '@chr/online-shared-ui-constants';
import { Translation, useTranslation } from '@chr/online-shared-ui-localizations';
import { Typography, UnstableSelectionSummaryField } from '@chr/eds-react';
import { Box, Stack } from '@mui/material';
import { ActionRequiredBanner } from '../../common/components/actionRequiredBanner';

export interface PayInvoiceProps {
  config: LocalConfig;
}

export interface PayInvoiceStatus {
  token: string,
  tokenError: boolean
}

const initialState: PayInvoiceStatus = {
  token: '',
  tokenError: false
}

const PayInvoice: React.FC<PayInvoiceProps> = ({ config }) => {
  const guestUser = React.useContext(GuestUserContext);
  const [state, setState] = useState(initialState);


  const translate = useTranslation();

  useEffect(() => {
    const checkUserHasSecurePayPermission = async() => {
      if (guestUser) {
        return;
      }

      const accessPolicies = await getPoliciesWithPermission(PermissionIds.SECURE_PAY, config.applicationId);

      if(accessPolicies.length === 0) {
        LinkService.getLinks().then(() => {
          window.location.assign(LinkService.PAGE_NOT_FOUND);
        });
      }
    }

    checkUserHasSecurePayPermission();
  }, [config, guestUser]);

  const handleContinue = async (event: FormEvent) => {
    event.preventDefault();
    let continueForm = event.target as HTMLFormElement;
    try {
      setState({token: '', tokenError: false});
      const token = await getPaymentAuthToken();
      setState({...state, token});
      continueForm.submit();
    } catch {
      setState({token: '', tokenError: true});
    }
  };


  const renderContent = () => {
    if (guestUser) {
      return <Redirect to="/" />;
    }
    return (
      <>
        <Stack alignItems={'center'} mb={4}>
          <Typography variant='h2'><Translation resourceKey='Pay your invoices' /></Typography>
          <Typography variant='body2'><Translation resourceKey='You will be redirected to our secure payment portal in a new tab.' /></Typography>
        </Stack>
          <Box mb={3}>
            <ActionRequiredBanner />
          </Box>
          <Stack direction={'row'} spacing={4} sx={{ alignItems: 'stretch' }}>
            <Box sx={{ minHeight: '136px', flex: 1, display: 'flex' }}>
              <UnstableSelectionSummaryField 
                id='usd-only' 
                label='' 
                subtitle={translate('USD only')} 
                dataPoint={[translate('Only invoices denominated in USD (U.S. Dollars) can be paid online.')]}
              />
            </Box>
            <Box sx={{ minHeight: '136px', flex: 1, display: 'flex' }}>
              <UnstableSelectionSummaryField 
                id='invoices-update' 
                label='' 
                subtitle={translate('Invoices update each morning')} 
                dataPoint={[translate('Invoices and updates appear in the payment portal at the start of the next business day.')]}
              />
            </Box>
            <Box sx={{ minHeight: '136px', flex: 1, display: 'flex' }}>
              <UnstableSelectionSummaryField 
                id='invoice-count-limit' 
                label='' 
                subtitle={translate('Invoice count limit')} 
                dataPoint={[translate('The secure payment portal can display up to 1,500 invoice records.')]}
              />
            </Box>
          </Stack>
          <div className="pay-invoice-container">
            {state.tokenError && <div className="error-block">
              <span className="ns-icon ns-error"><Translation resourceKey="There was an error retrieving the payment token" /></span></div>}
          <form action={`${config.paymentPortalUrl}/make-payment`} method="post" target="_blank" onSubmit={handleContinue}>
            <input type="hidden" name="authToken" value={state.token} />
            <button
              className="pay-invoice-continue-btn"
              type="submit"
            >
              <Translation resourceKey="Continue" />
              <svg
                className="external-link-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11M15 3H21V9M10 14L21 3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </form>
          </div>
      </>
    );  
  };

  return renderContent();
};

export default PayInvoice;
